import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import lazyImport from 'components/utils/lazyImport'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useMobileNativeAppAuth } from 'selectors/configSelectors'
import { clearAuthError, loginNativeApp } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { replace } from 'lib/routing/appRouting'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import HeroImage from 'content/components/HeroImage'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { trackClientEvent } from 'analytics/analytics'

// The auth forms are the bulk of this modal and are only needed once it opens,
// so they load as their own chunk rather than sitting in every page's entry.
const AccountAccessModalContent = lazyImport(
  () => import('./AccountAccessModalContent'),
  <LoadingIndicator visible floating />,
)

const HERO_HEIGHT = 200

const HeroLayer = styled.div`
  position: absolute;
  top: 0;
  inset-inline: 0;
  height: ${rem(HERO_HEIGHT)};
  overflow: hidden;
`

const StyledAccountAccessModal = styled(ModalBase)`
  &.has-mobile-hero {
    ${mediaQueryDown.mobile} {
      padding-top: ${rem(HERO_HEIGHT)};
    }
  }
`

interface Props {
  dismissable?: boolean
  initialMode?: App.UiAccountModalMode
  resetPasswordToken?: string
  resetPasswordTokenValid?: boolean
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  heroImageId?: string
  defaultEmail?: string
  callbackPath?: string
  defaultAppleData?: App.AppleLoginData
  hideLoginPasswordBackButton?: boolean
  /** Metasearch guest checkout: offers a "Continue without signing in" action on the login step. */
  guestCheckoutEnabled?: boolean
}

function AccountAccessModal(props: Props) {
  const {
    initialMode = 'login',
    resetPasswordToken,
    resetPasswordTokenValid = true,
    hotelOfferDetails,
    offerDetails,
    heroImageId,
    dismissable = true,
    defaultEmail,
    callbackPath,
    defaultAppleData,
    hideLoginPasswordBackButton,
    guestCheckoutEnabled,
  } = props

  const [mode, setMode] = useState<App.UiAccountModalMode>(initialMode)
  const [secondChanceDismissed, setSecondChanceDismissed] = useState(false)
  const loading = useAppSelector(state => state.auth.processing)

  const { resolve, afterClose } = useModalElementContext<AccountAccessModalResult>()
  const useNativeAppAuth = useAppSelector(useMobileNativeAppAuth)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loggedInError = useAppSelector(state => state.auth.error)
  const dispatch = useAppDispatch()
  const isSecondChanceMode = mode === 'promptAddPhone' || mode === 'promptAddPhoneOnboarding' || mode === 'joinOTP'
  const shouldShowHero = Boolean(heroImageId)
  const resolvedHeroImageId = heroImageId ?? ''

  useEffect(() => {
    dispatch(clearAuthError())
    return () => {
      dispatch(clearAuthError())
    }
  }, [dispatch])

  useEffect(() => {
    if (!isSecondChanceMode && secondChanceDismissed) {
      setSecondChanceDismissed(false)
    }
  }, [isSecondChanceMode, secondChanceDismissed])

  useEffect(() => {
    if (useNativeAppAuth) {
      dispatch(loginNativeApp())
    }
  }, [useNativeAppAuth, dispatch])

  useEffect(() => {
    if (useNativeAppAuth) {
      if (loggedIn) {
        resolve({ loggedIn: true })
        // afterClose **must** be called after resolve has finished resolving (it sets up state in after resolve)
        // 'resolve' resolves a promise, causing its result to be evalulated on next tick
        // To make afterClose happen after resolve, we bump it to the next tick as well
        window.setTimeout(() => afterClose(), 0)
      } else if (loggedInError) {
        resolve({ loggedIn: false })
        window.setTimeout(() => afterClose(), 0)
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeAppAuth, loggedIn, loggedInError])

  const afterCloseHandler = () => {
    if (callbackPath) {
      dispatch(replace(callbackPath))
    }
  }

  const handleSecondChanceClose = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.preventDefault()
    if (!secondChanceDismissed) {
      setSecondChanceDismissed(true)
      return
    }

    if (mode === 'promptAddPhoneOnboarding') {
      trackClientEvent({
        subject: 'phone_prompt_dismissed',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
      })
    }
    resolve({ loggedIn: true })
  }, [secondChanceDismissed, resolve, mode])

  if (useNativeAppAuth) {
    return null
  }

  return (<StyledAccountAccessModal
    size="S"
    dismissible={dismissable}
    modalId="accountAccess"
    onAfterClose={afterCloseHandler}
    onClose={isSecondChanceMode ? handleSecondChanceClose : undefined}
    className={shouldShowHero ? 'has-mobile-hero' : undefined}
  >
    <LoadingIndicator visible={loading} floating />
    {shouldShowHero && (
      <CSSBreakpoint max="mobile">
        <HeroLayer>
          <HeroImage
            id={resolvedHeroImageId}
            mobileAspectRatio="4:3"
            fit="center"
            loading="eager"
          />
        </HeroLayer>
      </CSSBreakpoint>
    )}
    <AccountAccessModalContent
      mode={mode}
      initialMode={initialMode}
      defaultEmail={defaultEmail}
      onModeChange={setMode}
      resetPasswordToken={resetPasswordToken}
      resetPasswordTokenValid={resetPasswordTokenValid}
      hotelOfferDetails={hotelOfferDetails}
      offerDetails={offerDetails}
      callbackPath={callbackPath}
      dismissable={dismissable}
      defaultAppleData={defaultAppleData}
      secondChanceDismissed={secondChanceDismissed}
      onSecondChanceDismissedChange={setSecondChanceDismissed}
      hideLoginPasswordBackButton={hideLoginPasswordBackButton}
      guestCheckoutEnabled={guestCheckoutEnabled}
    />
  </StyledAccountAccessModal>
  )
}

export default AccountAccessModal
